import { Component, OnInit, OnDestroy, ViewEncapsulation, inject } from '@angular/core';
import { ConfigService } from '../common/services/config.service';
import { MenuService } from '../common/services/menu.service';
import { SectionsService } from '../common/services/sections.service';
import { PageService } from '../common/services/page.service';
import { AuthService } from '../common/services/auth.service';
import { OrdersService } from '../common/services/orders.service';
import { APIService } from '../common/services/api.service';

import { Router, NavigationEnd, RouterLink, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';

import { GtagService } from '../common/services/gtag.service';
import { UserAccount, AccountType } from '../common/models/account';
import { FundraisingGroupsService } from '../common/services/fundraisingGroups.service';
import { CategoriesService } from '../common/services/categories.service';
import { DiscountsService } from '../common/services/discounts.service';
import { TaxesService } from '../common/services/taxes.service';
import { FreightService } from '../common/services/freight.service';
import { MessagesService } from '../common/services/messages.service';
import { CCFMessage } from '../common/models/messages';
import { makeFakeRouterLinks } from '../common/helpers/routerLinkReplacer';
import { AM_I_BOT } from '../common/helpers/whichBot';
import * as pluralize from 'pluralize';
import { StaffToolsComponent } from './staffTools/staffTools.component';
import { SearchBarComponent } from './search/searchBar.component';
import { CartIndicatorComponent } from './cart/indicator.component';
import { DesktopMenuBarComponent } from './menu/desktopMenuBar.component';
import { MobileMenuComponent } from './menu/mobileMenu.component';
import { SafePipe } from '../common/pipes/safePipe';

const generateLdJson = (data: Record<string, unknown>) =>
  `<script type="application/ld+json">${JSON.stringify(data, null, 2).replace(/<\/script>/g, '<\\/script>')}</script>`;
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.less'],
  providers: [],
  encapsulation: ViewEncapsulation.None,
  imports: [
    StaffToolsComponent,
    RouterLink,
    SearchBarComponent,
    CartIndicatorComponent,
    DesktopMenuBarComponent,
    MobileMenuComponent,
    RouterOutlet,
    SafePipe,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly apiService = inject(APIService);
  private readonly configService = inject(ConfigService);
  private readonly sectionsService = inject(SectionsService);
  private readonly menuService = inject(MenuService);
  private readonly authService = inject(AuthService);
  private readonly ordersService = inject(OrdersService);
  private readonly router = inject(Router);
  private readonly pageService = inject(PageService);
  private readonly gtagService = inject(GtagService);
  private readonly categoriesService = inject(CategoriesService);
  private readonly fundraisingGroupsService = inject(FundraisingGroupsService);
  private readonly discountsService = inject(DiscountsService);
  private readonly taxesService = inject(TaxesService);
  private readonly freightService = inject(FreightService);
  private readonly messagesService = inject(MessagesService);

  url = window.location.protocol + '//' + window.location.host;
  schema = generateLdJson({
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: this.url,
    potentialAction: {
      '@type': 'SearchAction',
      target: this.url + '/Search/{search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  });

  loggedIn: boolean = false;
  currentYear = new Date().getFullYear();
  private scrollVisible: boolean = false;
  private readonly scrollPoint: number = 1200; // This is about three rows of products

  holidayHours: boolean = false;
  jobsEnabled: boolean = false;

  onFrontPage: boolean = false;

  fundraisingActive: boolean = false;

  welcomeMessage: string = '';
  dashboardText: string = '';
  accountType: AccountType | null = null;
  userHome: string = '/';

  topMessage: CCFMessage | null = null;

  isBot: boolean = AM_I_BOT;
  private topMessageElement?: JQuery<HTMLElement>;

  private readonly _subscriptions: Subscription[] = [];

  constructor() {
    //TODO this guards against questionable suggestions
    pluralize.addUncountableRule('black');
    pluralize.addUncountableRule('white');
  }

  ngOnInit(): void {
    this._subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onFrontPage = event.urlAfterRedirects === '/';

          setTimeout(() => {
            // This makes sure the other titles probably go through as they are already in queue to be processed
            this.gtagService.pageView(this.pageService.getTitleString(), event.urlAfterRedirects);
          });
          this.endofRouting();
        }
      }),
    );

    this.trackScroll();
    document.addEventListener(
      'scroll',
      () => this.trackScroll(),
      self.CCF.supportsPassiveListeners ? { passive: true } : false,
    );

    // Because caching
    void this.configService.getAllConfig();

    // Used to show fundraising links
    void this.fundraisingGroupsService.fundraisingActive().then((result: boolean) => {
      // TODO subscription
      this.fundraisingActive = result;
    });

    // Used by the cart
    void this.configService.getLocations();
    // TODO promo this.DiscountsService.getDiscountsEnabled();
    void this.discountsService.refreshVolumeDiscounts();
    void this.taxesService.all();
    void this.freightService.reload();

    // Holiday hours and jobs enabled are not config settings
    void this.configService.getHolidayHoursEnabled().then((holidayHours: boolean) => {
      this.holidayHours = holidayHours; // TODO POST_LAUNCH this should be a subscription based thing
    });
    void this.configService.getJobsEnabled().then((jobsEnabled: boolean) => {
      this.jobsEnabled = jobsEnabled; // TODO this should be a subscription based thing
    });
    void this.sectionsService.all();
    void this.categoriesService.all();
    void this.menuService.getMenus();
    void this.menuService.getMobileMenus();
    void this.ordersService.loadAllCarts();
    void this.messagesService.loadSpecial().then((messages: CCFMessage[]) => {
      this.messagesLoaded(messages);
    });

    this._subscriptions.push(
      this.apiService.authenticatedDataStream().subscribe((data?: UserAccount | null) => {
        if (typeof data !== 'undefined') {
          if (data !== null) {
            if (!this.loggedIn) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              window.DD_RUM.onReady(() =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                window.DD_RUM.setUser({
                  id: data.username,
                  name: data.displayName,
                  email: data.email,
                  accountType: data.accountType,
                }),
              );
            }

            this.loggedIn = true;
            if (data.displayName) {
              this.welcomeMessage = 'Welcome ' + data.displayName + '!';
            }
            this.accountType = data.accountType;
            this.userHome = data.accountTypeURL;
            if (this.accountType === AccountType.Staff) {
              this.dashboardText = 'Go to Backend';
            } else if (this.accountType === AccountType.Wholesale) {
              this.dashboardText = 'Wholesale Dashboard';
            } else if (this.accountType === AccountType.Customer) {
              this.dashboardText = 'Customer Dashboard';
            }
          } else {
            if (this.loggedIn) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
              window.DD_RUM.onReady(() => window.DD_RUM.clearUser());
            }
            this.loggedIn = false;
            this.welcomeMessage = '';
            this.dashboardText = '';
            this.accountType = null;
            this.userHome = '/';
          }
        }
      }),
    );

    this.apiService.processRequests();
  }

  private endofRouting(): void {
    void this.messagesService.loadSpecial().then((messages: CCFMessage[]) => {
      this.messagesLoaded(messages);
    });

    this.apiService.processRequests();
  }

  private fakeRouterClick(event: JQueryEventObject) {
    const dest = jQuery(event.target).attr('href');

    if (dest) {
      void this.router.navigateByUrl(dest);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  private messagesLoaded(messages: CCFMessage[]): void {
    jQuery(document).off('click', 'a[fakerouter].inAppComponent');
    jQuery(document).on('click', 'a[fakerouter].inAppComponent', (event: JQueryEventObject) =>
      this.fakeRouterClick(event),
    );

    let newTopMessage: CCFMessage | undefined;
    for (const message of messages) {
      if (message.subject === 'Top Message') {
        newTopMessage = message;
      }
    }

    if (newTopMessage) {
      newTopMessage.content = makeFakeRouterLinks(newTopMessage.content);

      this.topMessage = newTopMessage;
    }

    setTimeout(() => {
      if (!this.topMessageElement || this.topMessageElement.length === 0) {
        this.topMessageElement = jQuery('#topMessageContainer');
      }

      if (newTopMessage) {
        this.topMessageElement.animate(
          {
            height: 'show',
          },
          1000,
        );
      } else {
        this.topMessageElement.animate(
          {
            height: 'hide',
          },
          1000,
          () => {
            this.topMessage = null;
          },
        );
      }
    }, 0);
  }

  login(): void {
    this.authService.promptLogin({ goHome: true });
  }

  logout(): void {
    void this.authService.logout();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription) => subscription.unsubscribe());

    document.removeEventListener('scroll', () => this.trackScroll(), false); // TODO this dosn't work
  }

  scrollToTop(): void {
    jQuery('html, body').animate({ scrollTop: 0 }, 'fast');
  }

  trackScroll(): void {
    const scroll = document.body.scrollTop || document.documentElement?.scrollTop || 0;
    if (scroll < this.scrollPoint && this.scrollVisible) {
      this.scrollVisible = false;
      jQuery('#scrollToTop').animate({ opacity: 0 }, 'fast');
    } else if (scroll >= this.scrollPoint && this.scrollVisible === false) {
      this.scrollVisible = true;
      jQuery('#scrollToTop').animate({ opacity: 1 }, 'fast');
    }
  }
}
